"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import styles from "./itinerary.module.css";

interface ItineraryDay {
  title: string;
  description?: string;
  photos?: { singleURL?: string; url?: string; title?: string } | null;
}

// Default fallback if we can't measure the header at runtime.
const STICKY_OFFSET_FALLBACK = 90;

export default function Itinerary({ days }: { days: ItineraryDay[] }) {
  const validDays = useMemo(
    () => days.filter((d) => d.title?.length > 0),
    [days],
  );

  const [activeIdx, setActiveIdx] = useState(0);
  const [visibleSet, setVisibleSet] = useState<Set<number>>(new Set([0]));
  const [stuck, setStuck] = useState(false);
  const [stripHeight, setStripHeight] = useState(0);
  const [stripGap, setStripGap] = useState(36);
  const [headerH, setHeaderH] = useState(STICKY_OFFSET_FALLBACK);

  const dayRefs = useRef<(HTMLLIElement | null)[]>([]);
  const pillRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const stripRef = useRef<HTMLDivElement | null>(null);
  const placeholderRef = useRef<HTMLDivElement | null>(null);

  // Measure the real sticky header height so the strip sits flush under it
  // on every breakpoint (mobile / tablet headers are shorter than desktop).
  useEffect(() => {
    const measure = () => {
      const el = document.querySelector(
        ".sticky-wrapper",
      ) as HTMLElement | null;
      const h = el?.getBoundingClientRect().height;
      if (h && h > 0) setHeaderH(Math.round(h));
    };

    measure();

    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    const target = document.querySelector(".sticky-wrapper");
    if (ro && target) ro.observe(target);

    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
      ro?.disconnect();
    };
  }, []);

  // Track which day is currently "active" based on which has crossed the
  // sticky header threshold from the top.
  useEffect(() => {
    if (!validDays.length) return;

    const onScroll = () => {
      // A day flips to active 140px before its top reaches the strip — the
      // approaching day lights up while the previous one is still mid-screen.
      const threshold = headerH + (stripHeight || 80) + 140;
      let bestIdx = 0;
      for (let i = 0; i < dayRefs.current.length; i++) {
        const el = dayRefs.current[i];
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top - threshold <= 0) bestIdx = i;
        else break;
      }
      setActiveIdx((prev) => (prev === bestIdx ? prev : bestIdx));
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [validDays.length, headerH, stripHeight]);

  // Reveal-on-enter animation for each day card.
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") {
      // No IO support? show all immediately.
      setVisibleSet(new Set(validDays.map((_, i) => i)));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        setVisibleSet((prev) => {
          let next: Set<number> | null = null;
          for (const entry of entries) {
            if (entry.isIntersecting) {
              const idx = Number(
                (entry.target as HTMLElement).dataset.idx ?? -1,
              );
              if (idx >= 0 && !prev.has(idx)) {
                if (!next) next = new Set(prev);
                next.add(idx);
              }
            }
          }
          return next ?? prev;
        });
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.12 },
    );

    dayRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [validDays.length]);

  // Keep the active pill scrolled into view inside the strip.
  useEffect(() => {
    const pill = pillRefs.current[activeIdx];
    const scroller = scrollerRef.current;
    if (!pill || !scroller) return;

    const pillRect = pill.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    const pillCenter = pill.offsetLeft + pill.offsetWidth / 2;
    const target = pillCenter - scroller.clientWidth / 2;

    // Only scroll the strip if the pill isn't already comfortably visible.
    const pillVisible =
      pillRect.left >= scrollerRect.left + 8 &&
      pillRect.right <= scrollerRect.right - 8;
    if (!pillVisible) {
      scroller.scrollTo({ left: target, behavior: "smooth" });
    }
  }, [activeIdx]);

  // Detect when the strip should detach into a full-width fixed bar. We use
  // a placeholder to preserve layout when it does, and hysteresis between
  // strip / placeholder rects to flip the state cleanly in both directions.
  useEffect(() => {
    const onScrollOrResize = () => {
      if (!stuck) {
        const el = stripRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        if (rect.top <= headerH + 0.5) {
          const cs = window.getComputedStyle(el);
          setStripHeight(el.offsetHeight);
          setStripGap(parseFloat(cs.marginBottom) || 36);
          setStuck(true);
        }
      } else {
        const ph = placeholderRef.current;
        if (!ph) return;
        const rect = ph.getBoundingClientRect();
        if (rect.top > headerH + 0.5) {
          setStuck(false);
        }
      }
    };

    onScrollOrResize();
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [stuck, headerH]);

  function scrollToDay(idx: number) {
    const el = dayRefs.current[idx];
    if (!el) return;
    const top =
      el.getBoundingClientRect().top +
      window.scrollY -
      headerH -
      (stripHeight || 70) -
      40;
    window.scrollTo({ top, behavior: "smooth" });
  }

  function stepStrip(direction: -1 | 1) {
    // Move to the next/prev day, then auto-scroll happens via the pill effect.
    const nextIdx = Math.max(
      0,
      Math.min(validDays.length - 1, activeIdx + direction),
    );
    if (nextIdx !== activeIdx) scrollToDay(nextIdx);
  }

  if (!validDays.length) return null;

  return (
    <div
      style={
        {
          ["--itinerary-strip-top" as string]: `${headerH}px`,
          ["--itinerary-scroll-margin" as string]: `${headerH + (stripHeight || 80)}px`,
        } as React.CSSProperties
      }
    >
      {/* Placeholder preserves layout when the strip detaches into fixed mode. */}
      <div
        ref={placeholderRef}
        className={styles.placeholder}
        style={{
          height: stuck ? stripHeight : 0,
          // strip's bottom margin is lost when it goes fixed; reproduce it here.
          marginBottom: stuck ? stripGap : 0,
        }}
        aria-hidden="true"
      />

      {/* ── Sticky Day Strip ── */}
      <div
        ref={stripRef}
        className={`${styles.strip} ${stuck ? styles.stripStuck : ""}`}
        role="tablist"
        aria-label="Itinerary day selector"
      >
        <div
          className={styles.brandBadge}
          aria-label={`Day ${activeIdx + 1} of ${validDays.length}`}
        >
          <i className={`fa-solid fa-route ${styles.brandIcon}`} aria-hidden="true"></i>
          <span className={styles.brandText}>
            <span className={styles.brandKicker}>Day</span>
            <span className={styles.brandCurrent}>
              {String(activeIdx + 1).padStart(2, "0")}
            </span>
            <span className={styles.brandSep}>/</span>
            <span className={styles.brandTotal}>
              {String(validDays.length).padStart(2, "0")}
            </span>
          </span>
        </div>

        <span className={styles.brandDivider} aria-hidden="true" />

        <button
          type="button"
          className={styles.arrow}
          onClick={() => stepStrip(-1)}
          disabled={activeIdx === 0}
          aria-label="Previous day"
        >
          <i className="fa-solid fa-chevron-left"></i>
        </button>

        <div className={styles.stripScroller} ref={scrollerRef}>
          {validDays.map((_, i) => {
            const isActive = i === activeIdx;
            const isPast = i < activeIdx;
            const iconClass = isPast
              ? "fa-solid fa-circle-check"
              : isActive
                ? "fa-solid fa-location-dot"
                : "fa-regular fa-circle";
            const iconStateClass = isPast
              ? styles.pillIconPast
              : isActive
                ? styles.pillIconCurrent
                : "";
            return (
              <button
                key={i}
                ref={(el) => {
                  pillRefs.current[i] = el;
                }}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`${styles.pill} ${isActive ? styles.pillActive : ""}`}
                onClick={() => scrollToDay(i)}
              >
                <i
                  className={`${iconClass} ${styles.pillIcon} ${iconStateClass}`}
                  aria-hidden="true"
                ></i>
                <span className={styles.pillLabel}>Day</span>
                <span>{String(i + 1).padStart(2, "0")}</span>
              </button>
            );
          })}
        </div>

        <button
          type="button"
          className={styles.arrow}
          onClick={() => stepStrip(1)}
          disabled={activeIdx >= validDays.length - 1}
          aria-label="Next day"
        >
          <i className="fa-solid fa-chevron-right"></i>
        </button>

        <div
          className={styles.progress}
          aria-hidden="true"
          role="presentation"
        >
          <div
            className={styles.progressFill}
            style={{
              width: `${((activeIdx + 1) / validDays.length) * 100}%`,
            }}
          />
        </div>
      </div>

      {/* ── Vertical Timeline ── */}
      <ol className={styles.timeline}>
        {validDays.map((day, i) => {
          const photoUrl = (day.photos?.singleURL || day.photos?.url || "")
            .toString()
            .replace(/ /g, "%20");
          const isActive = i === activeIdx;
          const isVisible = visibleSet.has(i);

          return (
            <li
              key={i}
              data-idx={i}
              ref={(el) => {
                dayRefs.current[i] = el;
              }}
              className={`${styles.day} ${isVisible ? styles.dayVisible : ""}`}
              style={{ transitionDelay: isVisible ? `${(i % 4) * 60}ms` : "0ms" }}
            >
              <div
                className={`${styles.dayMarker} ${isActive ? styles.dayMarkerActive : ""}`}
                aria-hidden="true"
              >
                <span className={styles.dayMarkerNum}>
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>

              <div
                className={`${styles.card} ${isActive ? styles.cardActive : ""} ${!photoUrl ? styles.cardNoImage : ""}`}
              >
                {photoUrl && (
                  <div className={styles.cardImage}>
                    <Image
                      src={photoUrl}
                      alt={day.title}
                      width={640}
                      height={480}
                      sizes="(max-width: 991px) 100vw, 320px"
                    />
                  </div>
                )}
                <div className={styles.cardBody}>
                  <span className={styles.dayKicker}>
                    Day {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className={styles.dayTitle}>{day.title}</h3>
                  {day.description && (
                    <div
                      className={styles.dayDescription}
                      dangerouslySetInnerHTML={{ __html: day.description }}
                    />
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
